import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { CLUB_PERKS, THREAD_FRIEND_URL } from "@/lib/club";
import { CHAT, WORDS } from "../concept-content";
import { useVideoCards, FOLLOW } from "../ConceptBits";
import { CLUB, EASE, Head, Ink, VIEW, PAGES } from "./bits";
import { LiveDates } from "./LiveDates";
import "./SectionsA.css";

/* Making, Club, Kind words. */

const TAPES = ["soft", "extra", "pop"] as const;
const TILT = [-3, 2, -1.5];

export function Making() {
  const cards = useVideoCards(3);
  return <section id="making" className="b2-sec b2-making" aria-labelledby="b2-making-title">
    <div className="b2-wrap">
      <div className="b2-row">
        <Head id="b2-making-title" hand="what I’m" word="making" tone="blue" />
        <div className="b2-row-aside">
          <p className="b2-note">new videos every week!</p>
          <svg className="b2-arrow" viewBox="0 0 80 60" aria-hidden="true"><Ink d="M6 8 C 30 6, 56 18, 64 48" delay={0.6} /><Ink d="M50 40 L65 50 L72 34" delay={1} /></svg>
          <Link to={PAGES.videos} className="b2-btn b2-btn-blue">All videos <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </div>

      <div className="b2-making-grid">
        {cards.map((c, i) => <motion.a key={c.id} className="b2-snap" href={c.url} target="_blank" rel="noreferrer"
          initial={{ opacity: 0, y: 40, rotate: TILT[i] + 6 }} whileInView={{ opacity: 1, y: 0, rotate: TILT[i] }} whileHover={{ y: -8, rotate: 0 }} viewport={VIEW}
          transition={{ delay: i * 0.1, type: "spring", stiffness: 140, damping: 16 }}>
          <i className={`b2-snap-tape b2-ktape-${TAPES[i]}`} aria-hidden="true" />
          <span className="b2-snap-img"><img src={c.img} alt="" style={{ objectPosition: c.pos }} /><b><Play size={18} fill="currentColor" aria-hidden="true" /></b></span>
          <span className="b2-snap-tag">{c.tag}</span>
          <span className="b2-snap-title">{c.title}</span>
        </motion.a>)}

        <div className="b2-follow">
          <span className="b2-follow-photo"><img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" /></span>
          <p className="b2-note">Cosmo approves every video.</p>
          <p className="b2-follow-label">Follow along</p>
          <ul>{FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}</ul>
        </div>
      </div>
    </div>
  </section>;
}

/* A real class: 0:15–1:00 of the raw capture, uncorrected, re-encoded silent
   at 960 px with a 1 s crossfade where it loops (44 s, about 1.7 MB). Nothing
   loads until the frame is within a screen of the viewport (the still first,
   then the video), and it only plays while visible. Reduced-motion visitors
   get the still alone. */
function ClassVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);
  const visible = useRef(false);
  const still = useRef(false);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    still.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const approach = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setNear(true); approach.disconnect(); } }, { rootMargin: "100% 0px" });
    const view = new IntersectionObserver(([e]) => {
      visible.current = e.isIntersecting;
      if (still.current || !v.currentSrc) return;
      if (e.isIntersecting) v.play().catch(() => {}); else v.pause();
    });
    approach.observe(v); view.observe(v);
    return () => { approach.disconnect(); view.disconnect(); };
  }, []);
  // The source arrives after the first observer fires, so start it here if it's already on screen.
  useEffect(() => { if (near && visible.current && !still.current) ref.current?.play().catch(() => {}); }, [near]);
  return <video ref={ref} src={near ? "/video/live-class.mp4" : undefined} poster={near ? "/video/live-class.jpg" : undefined} muted loop playsInline preload="none"
    aria-label="Chelsea teaching a live sewing class at her cutting table" />;
}

/** The live-class preview: a taped-up screen with the chat rolling in. */
export function OnAir() {
  const [n, setN] = useState(1);
  useEffect(() => { const t = setInterval(() => setN(v => v + 1), 2300); return () => clearInterval(t); }, []);
  const shown = Array.from({ length: Math.min(n, 3) }, (_, i) => { const k = n - Math.min(n, 3) + i; return { ...CHAT[k % CHAT.length], k }; });
  return <motion.figure className="b2-onair" initial={{ opacity: 0, y: 40, rotate: 5 }} whileInView={{ opacity: 1, y: 0, rotate: 2 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 16 }}>
    <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
    <span className="b2-onair-screen">
      <ClassVideo />
      <span className="b2-onair-live"><i />Live</span>
      <ul aria-label="Sample class chat">
        <AnimatePresence initial={false}>
          {shown.map(c => <motion.li key={c.k} layout initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, transition: { duration: 0.2 } }} transition={{ duration: 0.4, ease: EASE }}><b>{c.who}</b>{c.text}</motion.li>)}
        </AnimatePresence>
      </ul>
    </span>
    <figcaption>Tuesday night. Pajamas encouraged.</figcaption>
  </motion.figure>;
}

export function Club() {
  return <section id="club" className="b2-sec b2-club" aria-labelledby="b2-club-title">
    <div className="b2-wrap b2-club-grid">
      <div className="b2-club-copy">
        <p className="b2-kicker">Loose Threads Club · my Patreon</p>
        <Head id="b2-club-title" hand="come sew" word="with me" tone="pink" />
        <p className="b2-lede">Twice a month we pick one big project and I teach it live. Ask questions, sew along, and hang out with the nicest people on the internet.</p>
        <motion.ul className="b2-club-checks" initial="off" whileInView="on" viewport={VIEW}>
          {CLUB_PERKS.map((p, i) => <li key={p.title}>
            <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2.4" />
              <motion.path d="M7 13 L11.5 17.5 L19.5 8" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round"
                variants={{ off: { pathLength: 0, opacity: 0 }, on: { pathLength: 1, opacity: 1, transition: { delay: 0.4 + i * 0.25, duration: 0.45, opacity: { delay: 0.4 + i * 0.25, duration: 0.01 } } } }} /></svg>
            <span><b>{p.title}</b>{p.description}</span>
          </li>)}
        </motion.ul>
        <div className="b2-club-join">
          <span className="b2-youin-wrap">
            <Link to={CLUB} className="b2-btn b2-btn-yellow b2-btn-big">Join the club <ArrowRight size={18} aria-hidden="true" /></Link>
            <svg className="b2-youin-ring" viewBox="0 0 300 110" preserveAspectRatio="none" aria-hidden="true"><Ink d="M210 10 C 120 -4, 20 12, 8 52 C -2 92, 120 106, 220 98 C 300 90, 304 40, 260 18 C 240 8, 214 6, 190 9" delay={1.4} width={3} /></svg>
          </span>
          <Link to={CLUB} className="b2-btn b2-btn-ghost">See what’s inside</Link>
        </div>
        <p className="b2-club-foot">Just want to cheer me on? <a href={THREAD_FRIEND_URL}>Thread Friend is $3/month</a> ♡</p>
      </div>

      <div className="b2-club-side">
        <motion.p className="b2-price" initial={{ scale: 0, rotate: -40 }} whileInView={{ scale: 1, rotate: -12 }} viewport={VIEW} transition={{ delay: 0.5, type: "spring", stiffness: 220, damping: 13 }}>
          <b>$25</b>a month<small>cancel anytime</small>
        </motion.p>
        <OnAir />
        <LiveDates />
      </div>
    </div>
  </section>;
}

const STICKY = ["pop", "card", "extra"] as const;

export function KindWords() {
  return <section className="b2-sec b2-words" aria-labelledby="b2-words-title">
    <div className="b2-wrap">
      <Head id="b2-words-title" hand="a few" word="kind words" tone="blue" className="b2-head-center" />
      <div className="b2-words-grid">
        {WORDS.map((w, i) => <motion.figure key={w.name} className={`b2-sticky b2-sticky-${STICKY[i]}`}
          initial={{ opacity: 0, y: 40, rotate: [-4, 3, -2][i] + 6 }} whileInView={{ opacity: 1, y: 0, rotate: [-4, 3, -2][i] }} whileHover={{ rotate: 0, y: -6 }} viewport={VIEW}
          transition={{ delay: i * 0.12, type: "spring", stiffness: 130, damping: 15 }}>
          <i className={`b2-snap-tape b2-ktape-${["soft", "pop", "strong"][i]}`} aria-hidden="true" />
          <blockquote>“{w.quote}”</blockquote>
          <figcaption><b>{w.name}</b>{w.tag}</figcaption>
        </motion.figure>)}
      </div>
    </div>
  </section>;
}

